import { useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { createWorker } from "tesseract.js";

// Load the matching PDF.js worker from the React public folder.
// Copy node_modules/pdfjs-dist/legacy/build/pdf.worker.min.mjs
// into public/pdf.worker.min.mjs before building the React app.
const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
pdfjsLib.GlobalWorkerOptions.workerSrc = `${publicBase}/pdf.worker.min.mjs`;

const MONTH_NAMES =
  "janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre";
// OCR frequently inserts spaces inside dates (for example 01 / 06 / 2026).
const DATE_RE = new RegExp(
  String.raw`\b(\d{1,2}\s*[\/.-]\s*\d{1,2}\s*[\/.-]\s*\d{2,4}|\d{4}\s*-\s*\d{2}\s*-\s*\d{2}|\d{1,2}\s+(?:${MONTH_NAMES})\.?\s+\d{4}|(?:${MONTH_NAMES})\.?\s+\d{4})\b`,
  "gi",
);

const MONTHS = {
  janvier: 1,
  février: 2,
  fevrier: 2,
  mars: 3,
  avril: 4,
  mai: 5,
  juin: 6,
  juillet: 7,
  août: 8,
  aout: 8,
  septembre: 9,
  octobre: 10,
  novembre: 11,
  décembre: 12,
  decembre: 12,
};

function parseDate(raw, options = {}) {
  if (!raw) return null;
  const s = raw.trim().toLowerCase().replace(/\s+/g, "").replace(/\.$/, "");
  let y,
    m,
    d,
    isPeriod = false;
  const monthText = raw
    .trim()
    .toLowerCase()
    .replace(/\.$/, "")
    .replace(/\s+/g, " ");
  const dayMonth = monthText.match(/^(\d{1,2})\s+([a-zéûô]+)\s+(\d{4})$/i);
  const monthYear = monthText.match(/^([a-zéûô]+)\s+(\d{4})$/i);

  if (dayMonth && MONTHS[dayMonth[2]]) {
    d = +dayMonth[1];
    m = MONTHS[dayMonth[2]];
    y = +dayMonth[3];
  } else if (monthYear && MONTHS[monthYear[1]]) {
    if (!options.allowMonthYear) return null;
    m = MONTHS[monthYear[1]];
    y = +monthYear[2];
    d = 1;
    isPeriod = true;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    [y, m, d] = s.split("-").map(Number);
  } else {
    const parts = s.split(/[\/.-]/).map(Number);
    if (parts.length !== 3) return null;
    [d, m, y] = parts;
    if (y < 100) y += y >= 70 ? 1900 : 2000;
  }

  const value = new Date(Date.UTC(y, m - 1, d));
  if (
    value.getUTCFullYear() !== y ||
    value.getUTCMonth() !== m - 1 ||
    value.getUTCDate() !== d ||
    y < 1900 ||
    y > new Date().getFullYear() + 2
  )
    return null;
  return {
    date: `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    isPeriod,
  };
}

const DATE_LABELS = [
  {
    type: "signature",
    rank: 1,
    patterns: [
      /\bsign[ée]\s+(?:le|à)\s*$/i,
      /\bdate\s+de\s+signature\s*[:：-]?\s*$/i,
      /\bfait\s+(?:à|a)\b.{0,100}\ble\s*[:：-]?\s*$/i,
      // Many French forms use the shorter handwritten line: "À [ville], le ...".
      /(?:^|[^a-z])(?:à|a)\s+.{1,100}\ble\s*[:：-]?\s*$/i,
      /\blu\s+et\s+approuvé\b.{0,35}\ble\s*$/i,
      /\bsign[ée]\s+électroniquement\s+le\s*$/i,
    ],
  },
  {
    type: "receipt",
    rank: 2,
    patterns: [
      /\breçu[e]?\s+le\b/i,
      /\bdate\s+de\s+réception\s*[:：-]?\s*$/i,
      /\bdate\s+de\s+première\s+(?:entrée|presentation|présentation)\b.{0,90}$/i,
      /\bpremière\s+entrée\s+en\s+guichet\s+unique\b.{0,90}$/i,
      /\bdate\s+de\s+premier\s+enregistrement\b.{0,90}$/i,
      /\bdéposé[e]?\s+le\b/i,
      /\bdate\s+de\s+dépôt\b/i,
      /\benregistré[e]?\s+le\b/i,
      /\bdate\s+d['’]enregistrement\b/i,
      /\bdemande\s+reçue\s+le\b/i,
      /\bdossier\s+déposé\s+le\b/i,
      /\btransmis[e]?\s+le\b/i,
      /\bsoumis[e]?\s+le\b/i,
    ],
  },
  {
    type: "issue",
    rank: 3,
    patterns: [
      /\bétabli[e]?\s+(?:le|en)\b/i,
      /\bdélivré[e]?\s+le\b/i,
      /\bémis[e]?\s+le\b/i,
      /\bdate\s+d['’]émission\b/i,
      /\bdate\s+d['’]établissement\b/i,
      /\bdate\s+d['’]édition\b/i,
      /\bédité[e]?\s+le\b/i,
      /\bgénéré[e]?\s+le\b/i,
      /\bdélivré[e]?\s+par\b.{0,240}\ble\s*[:：-]?\s*$/i,
      /\bdate\s+d['’]émission\s*[:：-]?\s*$/i,
      /\bdate\s+d['’]établissement\s*[:：-]?\s*$/i,
    ],
  },
  {
    type: "document",
    rank: 4,
    patterns: [
      /\bdate\s+du\s+document\b/i,
      /\ben\s+date\s+du\b/i,
      /\bdocument\s+daté\s+du\b/i,
    ],
  },
];

function isExcludedDateContext(before, after) {
  const left = before.slice(-115);
  const right = after.slice(0, 55);
  // Exclude only when the candidate itself follows an exclusion label.
  // Do not reject a valid signature date merely because a DOB appears elsewhere on the page.
  if (
    /(?:date\s+de\s+naissance|né(?:e)?\s*(?:\([^)]*\))?\s+le|ne\s+le|born\s+on|birth\s*date)\s*[:：-]?\s*$/i.test(
      left,
    )
  )
    return true;
  if (
    /(?:date\s+d['’]expiration|date\s+de\s+validité|valable\s+jusqu['’]au|expire\s+le|expiration)\s*[:：-]?\s*$/i.test(
      left,
    )
  )
    return true;
  if (
    /(?:rendez-vous|rdv|appointment|convocation\s+pour)\s*[:：-]?\s*$/i.test(
      left,
    )
  )
    return true;
  // Vaccination dates are valid proof-of-presence dates and must NOT be excluded.
  // Version/printing dates on forms are incidental and should not be selected.
  if (
    /(?:version|édition|edite[e]?|mise\s+à\s+jour)\s+(?:du|le)\s*$/i.test(left)
  )
    return true;
  if (
    /^\s*(?:au|jusqu['’]au)\b/i.test(right) &&
    /date\s+de\s+validité|valable\s+du/i.test(left)
  )
    return true;
  if (/(?:transaction|opération bancaire)\s*[:：-]?\s*$/i.test(left))
    return true;
  return false;
}

function detectIncomeTaxYear(normalizedText) {
  const text = (normalizedText || "")
    .normalize("NFC")
    .replace(/[’‘]/g, "'")
    .replace(/imp[oô]t/g, "impôt")
    .replace(/déclarat[i1l]on/gi, "déclaration");

  // Tax notices: classify by the income year, not the later issue/printing date.
  const hasTaxNotice =
    /avis\s+d[' ]?impôt/i.test(text) ||
    /impôts?\s+sur\s+les\s+revenus\s+de/i.test(text);
  let match = hasTaxNotice
    ? text.match(
        /impôts?\s+sur\s+les\s+revenus\s+de\s*[:：-]?\s*((?:19|20)\d{2})/i,
      )
    : null;

  // Automatic income declarations (e.g. "DÉCLARATION AUTOMATIQUE — REVENUS 2020")
  // are also classified by the stated income year, even if no "impôt sur les revenus de"
  // phrase is present in the OCR text.
  if (!match && /déclaration\s+automatique/i.test(text)) {
    match = text.match(/\brevenus?\s*(?:de\s*)?((?:19|20)\d{2})\b/i);
  }

  if (!match) return null;
  const year = Number(match[1]);
  if (year < 1900 || year > new Date().getFullYear() + 2) return null;
  return {
    date: `${year}-12-31`,
    type: "tax_year",
    rank: 0,
    raw: `Année de revenus ${year}`,
    confidence: "high",
  };
}

function detectEvidenceDate(
  text,
  category,
  pageConfidence = 100,
  pageQuality = 100,
) {
  const normalized = (text || "")
    .normalize("NFC")
    .replace(/[’‘]/g, "'")
    .replace(/[\u00a0\t]+/g, " ")
    .replace(/[\r]+/g, "\n")
    .replace(/\s{2,}/g, " ")
    .replace(/(\d)\s+([\/.-])\s+(\d)/g, "$1$2$3")
    .replace(/(\d)\s+([\/.-])/g, "$1$2")
    .replace(/([\/.-])\s+(\d)/g, "$1$2");

  // Special tax-notice rule: the tax-income year is the classification year,
  // represented by 31 December of that year, even if the notice was issued later.
  const taxYear = detectIncomeTaxYear(normalized);
  if (taxYear) return taxYear;

  const vaccinationDocument =
    /(?:questionnaire.{0,80}vaccination|vaccination.{0,80}contre.{0,30}covid|certificat.{0,50}vaccination|eu\s+digital\s+covid|vaccin(?:ation)?\s+contre)/i.test(
      normalized,
    );
  const candidates = [];

  for (const match of normalized.matchAll(DATE_RE)) {
    const raw = match[0];
    const index = match.index || 0;
    const before = normalized.slice(Math.max(0, index - 190), index);
    const after = normalized.slice(
      index + raw.length,
      index + raw.length + 100,
    );
    if (isExcludedDateContext(before, after)) continue;

    const allowMonthYear =
      category === "payslip" ||
      /(?:relevé\s+(?:de\s+compte)?|échéancier|echeancier|période\s+couverte)/i.test(
        normalized,
      );
    const parsed = parseDate(raw, { allowMonthYear });
    if (!parsed) continue;

    const localBefore = before.slice(-155);
    const selected = DATE_LABELS.find((label) =>
      label.patterns.some((pattern) => pattern.test(localBefore)),
    );

    // Vaccination date is an accepted proof-of-presence date. On EU certificates
    // it is labelled "Date de la vaccination"; on some paper questionnaires it
    // appears as "Réservé au médecin — Date:". DOB and version dates remain excluded.
    const vaccinationLabel =
      /(?:date\s+de\s+(?:la\s+)?vaccination|date\s+vaccination|vaccination\s+date|date\s*[:：-]?\s*$)/i.test(
        localBefore,
      ) &&
      (/(?:vaccin|vaccination|certificat\s+covid|questionnaire)/i.test(
        normalized,
      ) ||
        /(?:réservé[e]?\s+au\s+médecin|reserved\s+for\s+doctor)/i.test(
          before.slice(-230),
        ));

    let evidence =
      vaccinationDocument && vaccinationLabel
        ? { type: "presence", rank: 5 }
        : selected;

    if (
      !evidence &&
      /attestation\s+d['’]?hébergement|attestation\s+d['’]?hebergement/i.test(
        normalized,
      ) &&
      /(?:^|[^a-z])(?:à|a)\s+.{0,120}\ble\s*[:：-]?\s*$/i.test(
        before.slice(-210),
      )
    ) {
      // Handwritten "À [ville] le [date]" line on an attestation d'hébergement.
      evidence = { type: "document", rank: 4 };
    }

    if (
      !evidence &&
      /attestation\s+de\s+demande\s+d['’]?asile|demande\s+d['’]?asile/i.test(
        normalized,
      ) &&
      /(?:date\s+de\s+première\s+entrée|première\s+entrée\s+en\s+guichet\s+unique|date\s+de\s+premier\s+enregistrement)\b.{0,90}$/i.test(
        localBefore,
      )
    ) {
      // On asylum certificates, the first-entry/registration date is preferred
      // over the later date on which the certificate was issued.
      evidence = { type: "receipt", rank: 2 };
    }

    if (
      !evidence &&
      /demande\s+d['’]?ouverture.{0,100}livret\s+[a4]|livret\s+[a4].{0,100}demande\s+d['’]?ouverture/i.test(
        normalized,
      ) &&
      /(?:^|[^a-z])(?:à|a)\s+.{0,100}\ble\s*[:：-]?\s*$/i.test(
        before.slice(-180),
      )
    ) {
      // Bank Livret A forms commonly place the handwritten signing date after
      // "À [ville], le" rather than beside a printed date label.
      evidence = { type: "signature", rank: 1 };
    }
    if (
      !evidence &&
      category === "payslip" &&
      /(?:bulletin\s+de\s+paie|bulletin\s+de\s+salaire|fiche\s+de\s+paie|période\s+de\s+paie|mois\s+de\s+paie|salaire\s+du\s+mois)/i.test(
        localBefore,
      )
    ) {
      evidence = { type: "pay_period", rank: 5 };
    }
    if (
      !evidence &&
      category !== "payslip" &&
      /(?:période\s+du|periode\s+du|période\s+couverte|relevé\s+(?:de\s+compte\s+)?du|releve\s+(?:de\s+compte\s+)?du|échéancier|echeancier)/i.test(
        localBefore,
      )
    ) {
      evidence = { type: "period", rank: 5 };
    }
    if (!evidence && /LIVRET_A_STAMP/i.test(normalized)) {
      // This OCR pass is restricted to the bank's lower-left date-stamp area;
      // its date is valid only after the full page has identified a Livret A form.
      evidence = { type: "bank_stamp", rank: 2 };
    }

    if (!evidence) continue;

    const readable = pageQuality >= 8 && /[A-Za-zÀ-ÿ]{3,}/.test(normalized);
    const confidence =
      readable &&
      (pageConfidence >= 20 ||
        evidence.rank <= 3 ||
        evidence.type === "presence")
        ? "high"
        : "medium";
    candidates.push({
      date: parsed.date,
      type: evidence.type,
      rank: evidence.rank,
      raw,
      confidence,
    });
  }

  candidates.sort((a, b) => a.rank - b.rank || a.date.localeCompare(b.date));
  return (
    candidates.find((candidate) => candidate.confidence === "high") || null
  );
}

function isContinuationPage(text) {
  const t = (text || "").toLowerCase().slice(0, 3200);
  return /(?:suite\s+de\s+votre\s+avis|suite\s+de\s+ce\s+document|(?:feuillet|page)\s*(?:n[°o]?\s*)?[:#]?\s*[2-9]\s*(?:\/|sur)\s*[2-9]|(?:page|feuillet)\s+suivant|suite\s+du\s+document)/i.test(
    t,
  );
}

function looksLikeNewDocument(text, pageIndex) {
  // Safer default: each page starts a new document unless the page itself
  // clearly identifies as a continuation. This prevents unreadable OCR on a
  // new document from merging it into the preceding document.
  if (pageIndex === 0) return true;
  return !isContinuationPage(text);
}

function groupPages(pages, category) {
  const groups = [];
  for (const page of pages) {
    if (!groups.length || page.startsDocument) groups.push({ pages: [page] });
    else groups[groups.length - 1].pages.push(page);
  }

  return groups.map((group, index) => {
    const dateCandidates = group.pages
      .map((page) =>
        page.dateInfo?.date
          ? page.dateInfo
          : detectEvidenceDate(page.text, category, page.ocrConfidence),
      )
      .filter((candidate) => candidate?.date)
      .sort((a, b) => a.rank - b.rank || a.date.localeCompare(b.date));
    const dateInfo = dateCandidates[0] || null;
    return {
      id: `document-${index + 1}`,
      pages: group.pages,
      date: dateInfo?.date || "",
      dateType: dateInfo?.type || "",
    };
  });
}

function ocrQuality(text, confidence) {
  const value = (text || "").trim();
  const letters = (value.match(/[A-Za-zÀ-ÿ]/g) || []).length;
  const words = value.match(/[A-Za-zÀ-ÿ]{3,}/g) || [];
  const usefulTerms =
    /attestation|délivré|délivrée|fait à|signature|revenus|impôt|domicile|vaccination|relevé|demande|asile|livret|échéancier|validité/i.test(
      value,
    )
      ? 30
      : 0;
  const dateLike =
    /\d{1,2}\s*[/. -]\s*\d{1,2}\s*[/. -]\s*\d{2,4}|\d{4}-\d{2}-\d{2}/.test(
      value,
    )
      ? 20
      : 0;
  return Math.min(
    100,
    words.length * 2 +
      Math.min(letters, 240) / 6 +
      usefulTerms +
      dateLike +
      Number(confidence || 0) / 10,
  );
}

async function recognizeBlob(blob, worker, mode = "6") {
  await worker.setParameters({ tessedit_pageseg_mode: mode });
  const result = await worker.recognize(blob);
  const text = result?.data?.text || "";
  const confidence = Number(result?.data?.confidence ?? 0);
  return { text, confidence, quality: ocrQuality(text, confidence), mode };
}

async function canvasToBlob(canvas, mimeType = "image/png", quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("Impossible de préparer l’image pour l’OCR.")),
      mimeType,
      quality,
    );
  });
}

async function recognizePdfPage(page, worker, scale = 4.5) {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  await page.render({ canvasContext: context, viewport }).promise;

  // Preserve the untouched render: aggressive thresholding was damaging thin
  // text and handwritten dates in the user's scanned attestations.
  const originalBlob = await canvasToBlob(canvas);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const gray =
      0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
    const enhanced = Math.max(0, Math.min(255, (gray - 118) * 1.18 + 128));
    pixels[i] = pixels[i + 1] = pixels[i + 2] = enhanced;
  }
  context.putImageData(imageData, 0, 0);
  const grayBlob = await canvasToBlob(canvas);

  const crop = async (
    yRatio,
    heightRatio,
    threshold = false,
    xRatio = 0,
    widthRatio = 1,
  ) => {
    const zone = document.createElement("canvas");
    const sourceX = Math.floor(canvas.width * xRatio);
    const sourceY = Math.floor(canvas.height * yRatio);
    const sourceWidth = Math.max(1, Math.floor(canvas.width * widthRatio));
    const sourceHeight = Math.max(1, Math.floor(canvas.height * heightRatio));
    zone.width = sourceWidth;
    zone.height = sourceHeight;
    const zctx = zone.getContext("2d", { willReadFrequently: true });
    zctx.drawImage(
      canvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      sourceWidth,
      sourceHeight,
    );
    if (threshold) {
      const data = zctx.getImageData(0, 0, zone.width, zone.height);
      // Mild threshold keeps faint printed characters; avoid hard black/white.
      for (let i = 0; i < data.data.length; i += 4) {
        const v = data.data[i] < 205 ? 25 : 245;
        data.data[i] = data.data[i + 1] = data.data[i + 2] = v;
      }
      zctx.putImageData(data, 0, 0);
    }
    const blob = await canvasToBlob(zone);
    zone.width = 1;
    zone.height = 1;
    return blob;
  };

  // Administrative dates and signatures are commonly in the lower quarter.
  const lowerDateBlock = await crop(0.58, 0.42, false);
  const lowerThreshold = await crop(0.58, 0.42, true);
  const upperHeading = await crop(0, 0.32, false);
  // Handwritten dates on French forms often sit in the middle fields (for
  // example after "né(e) le ... à ...") rather than in the signature block.
  const handwrittenFields = await crop(0.3, 0.42, false, 0.08, 0.84);
  // Some bank forms use a faint date stamp below the signature line. Isolate
  // that lower-left area so OCR is not distracted by the surrounding legal text.
  const lowerLeftStamp = await crop(0.82, 0.17, false, 0.08, 0.62);
  const attempts = [];
  attempts.push(await recognizeBlob(originalBlob, worker, "6"));
  attempts.push(await recognizeBlob(grayBlob, worker, "6"));
  attempts.push(await recognizeBlob(originalBlob, worker, "11"));
  attempts.push(await recognizeBlob(lowerDateBlock, worker, "6"));
  attempts.push(await recognizeBlob(lowerThreshold, worker, "11"));
  attempts.push(await recognizeBlob(upperHeading, worker, "6"));
  const handwrittenAttempt = await recognizeBlob(
    handwrittenFields,
    worker,
    "11",
  );
  attempts.push({ ...handwrittenAttempt, zone: "handwritten-fields" });
  const stampAttempt = await recognizeBlob(lowerLeftStamp, worker, "11");
  attempts.push({ ...stampAttempt, zone: "lower-left-stamp" });

  const best = [...attempts].sort((a, b) => b.quality - a.quality)[0];

  // First read the PDF's own text layer. This is much more accurate than OCR
  // whenever the uploaded PDF contains selectable text.
  const nativeText = (
    await page.getTextContent().catch(() => ({ items: [] }))
  ).items
    .map((item) => item.str || "")
    .filter(Boolean)
    .join(" ");

  // Keep each OCR pass independent. Combining all passes before date detection
  // can separate a date from its label and cause a valid date to be rejected.
  const ocrTexts = [
    ...(nativeText.trim()
      ? [
          {
            text: nativeText,
            confidence: 100,
            quality: ocrQuality(nativeText, 100),
            mode: "PDF text",
          },
        ]
      : []),
    ...attempts,
  ].filter((attempt) => attempt.text.trim());
  const combinedText = [
    ...(nativeText.trim() ? [`[PDF_TEXT] ${nativeText.trim()}`] : []),
    ...attempts
      .filter((attempt) => attempt.text.trim())
      .map(
        (attempt, index) => `[OCR_PASS_${index + 1}] ${attempt.text.trim()}`,
      ),
  ].join("\n");
  const bestConfidence = best?.confidence || 0;
  const combinedQuality = Math.max(...ocrTexts.map((a) => a.quality));
  canvas.width = 1;
  canvas.height = 1;
  return {
    text: combinedText,
    ocrTexts,
    confidence: bestConfidence,
    quality: combinedQuality,
    psm: best?.mode || "",
    attempts: attempts.length,
  };
}

async function processPdf(file, worker, setProgress) {
  // Keep this original buffer for pdf-lib. PDF.js receives a copy because it
  // may transfer/detach the ArrayBuffer while creating its worker document.
  const sourceBytes = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: sourceBytes.slice(0) })
    .promise;
  const pages = [];
  // PSM 6 works better for scanned administrative forms with aligned text blocks.
  await worker.setParameters({ tessedit_pageseg_mode: "6" });

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    setProgress(`Lecture de ${file.name} — page ${pageNumber}/${pdf.numPages}`);
    const page = await pdf.getPage(pageNumber);
    const recognized = await recognizePdfPage(page, worker);
    pages.push({
      key: `${file.name}::${pageNumber}`,
      sourceFile: file,
      sourceBytes,
      sourcePage: pageNumber - 1,
      pageNumber,
      text: recognized.text,
      ocrTexts: recognized.ocrTexts || [],
      ocrConfidence: recognized.confidence,
      ocrQuality: recognized.quality,
      ocrPsm: recognized.psm,
      ocrAttempts: recognized.attempts || 1,
      dateInfo: null,
      startsDocument: looksLikeNewDocument(recognized.text, pageNumber - 1),
    });
  }
  return pages;
}

async function processImage(file, worker, category, setProgress) {
  setProgress(`Lecture de ${file.name}`);
  const recognized = await recognizeBlob(file, worker);
  return [
    {
      key: `${file.name}::1`,
      sourceFile: file,
      imageBytes: await file.arrayBuffer(),
      sourcePage: 0,
      pageNumber: 1,
      text: recognized.text,
      ocrTexts: [
        {
          text: recognized.text,
          confidence: recognized.confidence,
          quality: recognized.quality,
          mode: recognized.mode,
        },
      ],
      ocrConfidence: recognized.confidence,
      ocrQuality: recognized.quality,
      ocrPsm: recognized.psm,
      ocrAttempts: recognized.attempts || 1,
      dateInfo: null,
      startsDocument: true,
    },
  ];
}

async function createPdfFromGroups(groups) {
  const output = await PDFDocument.create();
  const loadedSources = new Map();

  for (const group of groups) {
    for (const page of group.pages) {
      if (page.sourceBytes) {
        let source = loadedSources.get(page.sourceFile);
        if (!source) {
          source = await PDFDocument.load(page.sourceBytes.slice(0));
          loadedSources.set(page.sourceFile, source);
        }
        const [copiedPage] = await output.copyPages(source, [page.sourcePage]);
        output.addPage(copiedPage);
      } else if (page.imageBytes) {
        const extension = page.sourceFile.name.toLowerCase().split(".").pop();
        const image =
          extension === "png"
            ? await output.embedPng(page.imageBytes)
            : await output.embedJpg(page.imageBytes);
        const pdfPage = output.addPage([595.28, 841.89]);
        const scale = Math.min(535 / image.width, 780 / image.height);
        const width = image.width * scale;
        const height = image.height * scale;
        pdfPage.drawImage(image, {
          x: (595.28 - width) / 2,
          y: (841.89 - height) / 2,
          width,
          height,
        });
      }
    }
  }
  return new Blob([await output.save()], { type: "application/pdf" });
}

function downloadBlob(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function DocumentIntakeModal({ memberId, onClose, onUploaded }) {
  const [category, setCategory] = useState("");
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState("category");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [sortedBlob, setSortedBlob] = useState(null);
  const [unclassifiedBlob, setUnclassifiedBlob] = useState(null);
  const [counts, setCounts] = useState({
    sorted: 0,
    unclassified: 0,
    total: 0,
  });
  const [saved, setSaved] = useState(false);
  const [sortingReport, setSortingReport] = useState([]);
  const [reviewPages, setReviewPages] = useState([]);
  const [rejectedDates, setRejectedDates] = useState({});
  const [continuationPages, setContinuationPages] = useState({});
  const [chatPrompt, setChatPrompt] = useState("");
  const [chatModalOpen, setChatModalOpen] = useState(false);

  const categoryLabel = useMemo(
    () =>
      ({
        other: "Autres documents",
        presence: "Preuves de présence",
        payslip: "Fiches de paie",
      })[category] || "",
    [category],
  );

  const selectFiles = (list) => {
    const selected = Array.from(list || []);
    const valid = selected.filter((file) =>
      /\.(pdf|jpe?g|png)$/i.test(file.name),
    );
    if (valid.length !== selected.length)
      toast.error("Seuls les fichiers PDF, JPG et PNG sont acceptés.");
    setFiles((current) => [...current, ...valid]);
  };

  const buildChatGPTPrompt = () => {
    const names = files.map((file) => `- ${file.name}`).join("\n");
    return `Je joins un ou plusieurs documents administratifs français à analyser pour UNITED MIGRANTS.

MISSION
Lis visuellement toutes les pages des fichiers joints. Ne te limite pas au texte OCR. Agrandis et inspecte les zones de dates, signatures, tampons et en-têtes si nécessaire.

POUR CHAQUE DOCUMENT
1. Identifie le type exact du document.
2. Repère la date pertinente pour le classement administratif.
3. Écarte les dates de naissance, d'expiration/validité, de rendez-vous, de paiement ou de simple référence historique.
4. Attestation d'élection de domicile : privilégie la date après « Fait à ..., le ... ».
5. Attestation d'hébergement : lis la date manuscrite sur la ligne « À ..., le ... ».
6. Avis/déclaration fiscale : utilise l'année des revenus indiquée, représentée par le 31 décembre de cette année.
7. Certificat COVID : utilise la date de vaccination.
8. Questionnaire de vaccination : examine la date manuscrite dans la zone réservée au médecin.
9. Demande d'ouverture de Livret A : examine le cachet bancaire daté.
10. Attestation de demande d'asile : privilégie la date de première entrée/enregistrement au guichet unique, pas la date de délivrance.

RÈGLES
- Classe du plus ancien au plus récent.
- Garde ensemble les pages d'un même document multipage et dans leur ordre original.
- Ne sépare pas les pages 1/2 et 2/2 d'un avis fiscal.
- Ne devine jamais un chiffre illisible. Place le document dans le lot non classé si aucune date pertinente ne peut être déterminée.
- Ne supprime, ne duplique et ne modifie aucune page.

FICHIERS ATTENDUS
A. documents_tries_chronologiquement.pdf : tous les documents datés, classés du plus ancien au plus récent.
B. documents_non_classes.pdf : tous les documents sans date pertinente déterminable.
Fournis aussi un tableau avec le numéro de page d'origine, le type de document, la date retenue et le motif. Vérifie que le nombre total de pages des deux PDF correspond exactement au nombre de pages des fichiers joints.

Fichiers sélectionnés dans UNITED MIGRANTS :
${names}

Je vais joindre manuellement les fichiers à cette conversation. Attends que les pièces jointes soient présentes avant de commencer.`;
  };

  const openChatGPT = async () => {
    if (!files.length) return;
    const prompt = buildChatGPTPrompt();
    setChatPrompt(prompt);
    setChatModalOpen(true);
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success(
        "Instructions copiées. Vous pourrez les coller dans ChatGPT.",
      );
    } catch {
      toast(
        "Les instructions sont prêtes dans cette fenêtre. Copiez-les avant de continuer.",
      );
    }
  };

  const continueToChatGPT = () => {
    // This is the only action that leaves UNITED MIGRANTS.
    // ChatGPT cannot be embedded as a personal logged-in session in this modal.
    window.open("https://chatgpt.com/", "_blank", "noopener,noreferrer");
  };

  const copyChatGPTPrompt = async () => {
    if (!chatPrompt) return;
    try {
      await navigator.clipboard.writeText(chatPrompt);
      toast.success("Instructions copiées.");
    } catch {
      toast.error(
        "Copie automatique impossible. Sélectionnez le texte et copiez-le.",
      );
    }
  };

  const uploadOther = async () => {
    setBusy(true);
    const toastId = toast.loading("Envoi des documents...");
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        form.append("member_id", String(memberId));
        form.append("document_category", "other");
        const response = await axios.post("/api/uploadDocument.php", form);
        if (!response.data?.success)
          throw new Error(response.data?.error || `Échec : ${file.name}`);
      }
      toast.success("Documents ajoutés.", { id: toastId });
      await onUploaded?.();
      onClose?.();
    } catch (error) {
      toast.error(error.message || "Erreur serveur.", { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  const analyzeAndSort = async () => {
    if (!files.length) return;
    setBusy(true);
    setStage("processing");
    setProgress("Lecture locale des documents par OCR…");
    let worker;

    try {
      worker = await createWorker("fra+eng", 1, {
        logger: (message) => {
          if (message.status === "recognizing text") {
            setProgress(
              `OCR complémentaire : ${Math.round((message.progress || 0) * 100)} %`,
            );
          } else if (message.status) {
            setProgress(message.status);
          }
        },
      });

      const allPages = [];
      for (const file of files) {
        if (/\.pdf$/i.test(file.name)) {
          const pages = await processPdf(file, worker, setProgress);
          pages.forEach((page) => {
            const sources = page.ocrTexts?.length
              ? page.ocrTexts
              : [
                  {
                    text: page.text,
                    confidence: page.ocrConfidence,
                    quality: page.ocrQuality,
                  },
                ];
            const allOcrText = sources
              .map((source) => source.text || "")
              .join(" ");
            const isLivretAForm =
              /demande\s+d['’]?ouverture.{0,100}livret\s+[a4]|livret\s+[a4].{0,100}demande\s+d['’]?ouverture/i.test(
                allOcrText,
              );
            const candidates = sources
              .map((source) =>
                detectEvidenceDate(
                  isLivretAForm && source.zone === "lower-left-stamp"
                    ? `LIVRET_A_STAMP ${source.text}`
                    : source.text,
                  category,
                  source.confidence,
                  source.quality,
                ),
              )
              .filter(Boolean)
              .sort((a, b) => a.rank - b.rank || a.date.localeCompare(b.date));
            // Retain Tesseract's best candidate only as a supplementary fallback.
            page.ocrDateInfo = candidates[0] || null;
            page.dateInfo = null;
          });
          allPages.push(...pages);
        } else {
          const imagePages = await processImage(
            file,
            worker,
            category,
            setProgress,
          );
          imagePages.forEach((page) => {
            page.ocrDateInfo = detectEvidenceDate(
              page.text,
              category,
              page.ocrConfidence,
              page.ocrQuality,
            );
          });
          allPages.push(...imagePages);
        }
      }

      if (!allPages.length) throw new Error("Aucune page n'a pu être lue.");

      // OpenAI API is disabled in this build. All date candidates come
      // from browser-side OCR; no page is sent to an external AI service.
      for (let index = 0; index < allPages.length; index += 1) {
        const page = allPages[index];
        setProgress(
          `Classement OCR local : page ${index + 1}/${allPages.length}`,
        );
        page.dateInfo = page.ocrDateInfo || null;
        page.recognitionSource = page.dateInfo
          ? "OCR local"
          : "OCR local — aucune date détectée";
      }

      if (!allPages.length) throw new Error("Aucune page n'a pu être lue.");
      setReviewPages(allPages);
      setRejectedDates({});
      setContinuationPages(
        Object.fromEntries(
          allPages
            .filter((page) => !page.startsDocument)
            .map((page) => [page.key, true]),
        ),
      );
      setStage("review");
      toast.success(
        "Analyse terminée : vérifiez les pages avant de créer les PDF.",
      );
    } catch (error) {
      console.error("Document sorting error:", error);
      toast.error(`Tri impossible : ${error.message || "erreur OCR/PDF"}`);
      setStage("select");
    } finally {
      if (worker) await worker.terminate();
      setBusy(false);
      setProgress("");
    }
  };

  const finalizeSorting = async () => {
    setBusy(true);
    try {
      const pages = reviewPages.map((page) => ({
        ...page,
        // A page without an automatically detected date is always kept in
        // the non-classified PDF. No manual date entry is possible.
        dateInfo: rejectedDates[page.key] ? null : page.dateInfo,
        startsDocument:
          page.pageNumber === 1 || !Boolean(continuationPages[page.key]),
      }));

      const groups = groupPages(pages, category);
      const datedGroups = groups.filter((group) => group.date);
      const undatedGroups = groups.filter((group) => !group.date);
      datedGroups.sort((a, b) => a.date.localeCompare(b.date));

      const originalKeys = pages.map((page) => page.key);
      const outputKeys = [...datedGroups, ...undatedGroups].flatMap((group) =>
        group.pages.map((page) => page.key),
      );
      if (
        outputKeys.length !== originalKeys.length ||
        new Set(outputKeys).size !== outputKeys.length ||
        originalKeys.some((key) => !outputKeys.includes(key))
      ) {
        throw new Error(
          "Vérification d’intégrité échouée : une page est manquante ou dupliquée.",
        );
      }
      for (const group of groups) {
        const positions = group.pages.map((page) => pages.indexOf(page));
        if (
          positions.some(
            (position, i) => i > 0 && position !== positions[i - 1] + 1,
          )
        ) {
          throw new Error(
            "Vérification d’intégrité échouée : l’ordre interne d’un document a été modifié.",
          );
        }
      }

      setProgress("Création des deux fichiers PDF…");
      const [sorted, unclassified] = await Promise.all([
        createPdfFromGroups(datedGroups),
        createPdfFromGroups(undatedGroups),
      ]);
      setSortedBlob(sorted);
      setUnclassifiedBlob(unclassified);

      const makeReportRow = (page, group, status) => ({
        page: page.pageNumber,
        file: page.sourceFile.name,
        date: status === "classified" ? group.date : "",
        type: status === "classified" ? group.dateType : "",
        confidence: `${Math.round(page.ocrConfidence || 0)}%`,
        quality: Math.round(page.ocrQuality || 0),
        ocrMode: page.ocrPsm || "",
        ocrAttempts: page.ocrAttempts || 1,
        detectedText: (page.text || "").replace(/\s+/g, " ").slice(0, 500),
        status,
        group: group.id,
      });
      setSortingReport([
        ...datedGroups.flatMap((group) =>
          group.pages.map((page) => makeReportRow(page, group, "classified")),
        ),
        ...undatedGroups.flatMap((group) =>
          group.pages.map((page) =>
            makeReportRow(page, group, "non-classified"),
          ),
        ),
      ]);
      setCounts({
        sorted: datedGroups.reduce((sum, group) => sum + group.pages.length, 0),
        unclassified: undatedGroups.reduce(
          (sum, group) => sum + group.pages.length,
          0,
        ),
        total: pages.length,
      });
      setSaved(false);
      setStage("results");
      toast.success("Les deux PDF sont prêts.");
    } catch (error) {
      console.error("Document sorting finalization error:", error);
      toast.error(error.message || "Impossible de créer les PDF.");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const saveBothPdfs = async () => {
    if (!sortedBlob || !unclassifiedBlob) return;
    setBusy(true);
    const toastId = toast.loading(
      "Enregistrement des deux PDF dans le dossier membre…",
    );
    try {
      const date = new Date().toISOString().slice(0, 10);
      const outputs = [
        {
          blob: sortedBlob,
          filename: `Documents_tries_${category}_${date}.pdf`,
          status: "sorted",
        },
        {
          blob: unclassifiedBlob,
          filename: `Pages_rejetees_${category}_${date}.pdf`,
          status: "unclassified",
        },
      ].filter((item) => item.blob.size > 0);

      for (const item of outputs) {
        const file = new File([item.blob], item.filename, {
          type: "application/pdf",
        });
        const form = new FormData();
        form.append("file", file);
        form.append("member_id", String(memberId));
        form.append("document_category", category);
        form.append("classification_status", item.status);
        const response = await axios.post("/api/uploadDocument.php", form);
        if (!response.data?.success) {
          throw new Error(
            response.data?.error || `Impossible d'enregistrer ${item.filename}`,
          );
        }
      }

      setSaved(true);
      toast.success("Les PDF ont été enregistrés dans le dossier membre.", {
        id: toastId,
      });
      await onUploaded?.();
    } catch (error) {
      console.error("Saving sorted PDFs failed:", error);
      toast.error(error.message || "Erreur lors de l'enregistrement des PDF.", {
        id: toastId,
      });
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    onClose?.();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-intake-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(15,23,42,.62)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "min(760px,100%)",
          maxHeight: "92vh",
          overflow: "auto",
          background: "#fff",
          color: "#172033",
          borderRadius: 14,
          padding: 22,
          boxShadow: "0 20px 60px rgba(0,0,0,.28)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <h2 id="document-intake-title" style={{ margin: 0 }}>
            {stage === "processing"
              ? "Tri des documents en cours"
              : stage === "results"
                ? "Vos deux PDF sont prêts"
                : stage === "review"
                  ? "Vérifier le classement"
                  : "Ajouter des documents"}
          </h2>
          <button
            type="button"
            onClick={closeModal}
            disabled={busy}
            style={{
              width: 36,
              height: 36,
              border: "1px solid #d1d5db",
              borderRadius: 8,
              background: "#f3f4f6",
              color: "#111827",
              fontSize: 20,
            }}
          >
            ×
          </button>
        </div>

        {stage === "category" && (
          <>
            <p>Choisissez le type de document à traiter.</p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
                gap: 12,
              }}
            >
              {[
                ["other", "Autres documents", "Téléversement classique"],
                [
                  "presence",
                  "Preuves de présence",
                  "Créer automatiquement deux PDF",
                ],
                [
                  "payslip",
                  "Fiches de paie",
                  "Trier selon la période de paie détectée",
                ],
              ].map(([value, title, description]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setCategory(value);
                    setStage("select");
                  }}
                  style={{
                    textAlign: "left",
                    padding: 16,
                    border: "1px solid #cbd5e1",
                    borderRadius: 10,
                    background: "#fff",
                    color: "#172033",
                    minHeight: 100,
                    whiteSpace: "normal",
                  }}
                >
                  <strong style={{ display: "block", fontSize: 15 }}>
                    {title}
                  </strong>
                  <span
                    style={{
                      display: "block",
                      marginTop: 8,
                      fontSize: 13,
                      lineHeight: 1.4,
                      color: "#475569",
                    }}
                  >
                    {description}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {stage === "select" && (
          <>
            <p>
              <strong>Catégorie :</strong> {categoryLabel}
            </p>
            <p>
              Ajoutez vos documents PDF, JPG ou PNG. Ouvrez ChatGPT pour une
              analyse visuelle approfondie, ou utilisez le tri OCR local.
              <strong>
                {" "}
                Cette version n'envoie plus aucune page à l'API OpenAI et ne
                consomme donc plus de crédits API pour cette fonction.
              </strong>
            </p>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(event) => {
                selectFiles(event.target.files);
                event.target.value = "";
              }}
            />
            {files.length > 0 && (
              <ul style={{ paddingLeft: 22 }}>
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} style={{ margin: "8px 0" }}>
                    {file.name} — {Math.ceil(file.size / 1024)} Ko{" "}
                    <button
                      type="button"
                      onClick={() =>
                        setFiles((current) =>
                          current.filter((_, i) => i !== index),
                        )
                      }
                    >
                      Retirer
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                marginTop: 20,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setCategory("");
                  setFiles([]);
                  setStage("category");
                }}
              >
                Retour
              </button>
              {category === "other" ? (
                <button
                  type="button"
                  disabled={!files.length || busy}
                  onClick={uploadOther}
                >
                  Envoyer les fichiers
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={!files.length || busy}
                    onClick={openChatGPT}
                    style={{ background: "#166534", color: "#fff" }}
                  >
                    Préparer l’analyse avec ChatGPT
                  </button>
                  <button
                    type="button"
                    disabled={!files.length || busy}
                    onClick={analyzeAndSort}
                  >
                    Trier avec OCR local
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {stage === "processing" && (
          <div style={{ padding: "28px 0" }}>
            <p>
              <strong>Analyse de toutes les pages…</strong>
            </p>
            <p>
              {progress ||
                "Lecture des pages et recherche des dates pertinentes."}
            </p>
            <p style={{ color: "#475569", fontSize: 13 }}>
              Les pages sont analysées dans votre navigateur. Les fichiers
              originaux ne sont jamais modifiés.
            </p>
            <div
              style={{
                height: 8,
                background: "#e5e7eb",
                borderRadius: 20,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: "55%",
                  background: "#ea580c",
                  animation: "pulse 1.5s infinite",
                }}
              />
            </div>
          </div>
        )}

        {stage === "review" && (
          <>
            <p>
              <strong>Vérification des dates avant classement</strong>
            </p>
            <p style={{ color: "#475569", fontSize: 13 }}>
              Le système utilise uniquement les dates reconnues automatiquement.
              Les pages avec une date fiable seront classées dans le premier PDF
              ; toutes les pages sans date fiable seront conservées dans le
              second. Aucune saisie manuelle n’est nécessaire. Vous pouvez
              toujours réunir les pages d’un même document.
            </p>
            <div
              style={{
                maxHeight: "48vh",
                overflowY: "auto",
                display: "grid",
                gap: 10,
              }}
            >
              {reviewPages.map((page) => {
                const auto = page.dateInfo?.date ? page.dateInfo : null;
                const vision = null;
                return (
                  <div
                    key={page.key}
                    style={{
                      border: "1px solid #dbe2ea",
                      borderRadius: 9,
                      padding: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <strong>
                        {page.sourceFile.name} — page {page.pageNumber}
                      </strong>
                      <span
                        style={{
                          fontSize: 12,
                          color: auto ? "#15803d" : "#b45309",
                        }}
                      >
                        {auto
                          ? `Date détectée : ${auto.date} (${auto.type})${auto.source ? ` — ${auto.source}` : ""}`
                          : "Aucune date fiable détectée"}
                      </span>
                    </div>
                    {auto && (
                      <label
                        style={{
                          display: "flex",
                          gap: 7,
                          alignItems: "center",
                          marginTop: 7,
                          fontSize: 12,
                          color: "#9a3412",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(rejectedDates[page.key])}
                          onChange={(e) =>
                            setRejectedDates((current) => ({
                              ...current,
                              [page.key]: e.target.checked,
                            }))
                          }
                        />
                        Ne pas utiliser cette date détectée (la garder non
                        classée)
                      </label>
                    )}
                    <p
                      style={{
                        margin: "9px 0 0",
                        fontSize: 12,
                        color: "#475569",
                      }}
                    >
                      {auto
                        ? `Date automatique retenue : ${auto.date} — ${auto.type}${auto.evidence ? ` — preuve : ${auto.evidence}` : ""}${auto.reason ? ` — ${auto.reason}` : ""}`
                        : `Cette page sera conservée dans le PDF des pages non classées.${page.recognitionSource ? ` (${page.recognitionSource})` : ""}`}
                    </p>
                    {vision && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: 9,
                          borderRadius: 7,
                          background: "#fff7ed",
                          border: "1px solid #fed7aa",
                          fontSize: 12,
                          color: "#7c2d12",
                        }}
                      >
                        <strong>Diagnostic AI Vision</strong>
                        <div>
                          Document identifié :{" "}
                          {vision.documentType || "non précisé"}
                        </div>
                        <div>
                          Date proposée :{" "}
                          {vision.candidateDate ||
                            vision.visibleDateText ||
                            "aucune"}
                        </div>
                        <div>Confiance : {vision.confidence}</div>
                        <div>
                          Libellé associé : {vision.evidence || "aucun"}
                        </div>
                        <div>Motif : {vision.reason || "non communiqué"}</div>
                        {vision.excluded && (
                          <div>Date signalée comme exclue.</div>
                        )}
                      </div>
                    )}
                    {page.pageNumber > 1 && (
                      <label
                        style={{
                          display: "flex",
                          gap: 7,
                          alignItems: "center",
                          marginTop: 9,
                          fontSize: 12,
                          color: "#475569",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(continuationPages[page.key])}
                          onChange={(e) =>
                            setContinuationPages((current) => ({
                              ...current,
                              [page.key]: e.target.checked,
                            }))
                          }
                        />
                        Cette page continue le document de la page précédente
                      </label>
                    )}
                    {page.text?.trim() && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: "pointer", fontSize: 12 }}>
                          Voir le texte reconnu par OCR
                        </summary>
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            fontSize: 11,
                            maxHeight: 110,
                            overflow: "auto",
                            background: "#f8fafc",
                            padding: 8,
                          }}
                        >
                          {page.text.slice(0, 1600)}
                        </pre>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
            <p style={{ fontSize: 12, color: "#64748b" }}>
              Aucune date ne sera saisie manuellement. Les cases de continuation
              permettent de garder ensemble les documents multipages, par
              exemple un avis d’impôt 1/2 et 2/2. Les pages sans date reconnue
              ne sont pas supprimées : elles seront placées dans le PDF non
              classé.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                marginTop: 16,
              }}
            >
              <button
                type="button"
                disabled={busy}
                onClick={() => setStage("select")}
              >
                Retour
              </button>
              <button type="button" disabled={busy} onClick={finalizeSorting}>
                Confirmer et créer les deux PDF
              </button>
            </div>
          </>
        )}

        {stage === "results" && (
          <>
            <p>
              Le traitement est terminé. Toutes les pages sont réparties entre
              les deux fichiers ci-dessous.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                gap: 12,
                margin: "18px 0",
              }}
            >
              <div
                style={{
                  border: "1px solid #bbf7d0",
                  background: "#f0fdf4",
                  borderRadius: 10,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 25 }}>📅</div>
                <strong>PDF trié par date</strong>
                <div style={{ fontSize: 26, fontWeight: 700, margin: "8px 0" }}>
                  {counts.sorted} page(s)
                </div>
                <p style={{ fontSize: 13, color: "#475569" }}>
                  Dates fiables détectées — du plus ancien au plus récent.
                </p>
                {sortedBlob?.size > 0 ? (
                  <button
                    type="button"
                    onClick={() =>
                      downloadBlob(
                        sortedBlob,
                        `Documents_tries_${category}_${today}.pdf`,
                      )
                    }
                  >
                    Télécharger le PDF trié
                  </button>
                ) : (
                  <p style={{ fontSize: 13 }}>
                    Aucune page avec date fiable détectée.
                  </p>
                )}
              </div>
              <div
                style={{
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: 10,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 25 }}>📂</div>
                <strong>PDF des pages rejetées / non classées</strong>
                <div style={{ fontSize: 26, fontWeight: 700, margin: "8px 0" }}>
                  {counts.unclassified} page(s)
                </div>
                <p style={{ fontSize: 13, color: "#475569" }}>
                  Dates non reconnues automatiquement — pages conservées dans
                  leur ordre d’origine.
                </p>
                {unclassifiedBlob?.size > 0 ? (
                  <button
                    type="button"
                    onClick={() =>
                      downloadBlob(
                        unclassifiedBlob,
                        `Pages_rejetees_${category}_${today}.pdf`,
                      )
                    }
                  >
                    Télécharger le PDF des pages rejetées
                  </button>
                ) : (
                  <p style={{ fontSize: 13 }}>
                    Toutes les pages ont une date fiable.
                  </p>
                )}
              </div>
            </div>
            <p style={{ fontSize: 13, color: "#475569" }}>
              Total traité : {counts.total} page(s). Vérification :{" "}
              {counts.sorted + counts.unclassified === counts.total
                ? "toutes les pages sont réparties exactement une fois."
                : "erreur de répartition détectée."}{" "}
              Les pages d'un même groupe restent ensemble lorsque la séparation
              entre documents est reconnue.
            </p>
            <button
              type="button"
              onClick={() => {
                const lines = [
                  "Fichier;Page originale;Date retenue;Type;Confiance OCR;Qualité OCR;Mode OCR;Texte OCR (extrait);Classement;Groupe",
                  ...sortingReport.map((item) =>
                    [
                      item.file,
                      item.page,
                      item.date,
                      item.type,
                      item.confidence,
                      item.quality,
                      item.ocrMode,
                      item.ocrAttempts,
                      item.detectedText,
                      item.status,
                      item.group,
                    ]
                      .map(
                        (value) =>
                          `"${String(value || "")
                            .replaceAll('"', '""')
                            .replace(/[\\r\\n]+/g, " ")}"`,
                      )
                      .join(";"),
                  ),
                ];
                downloadBlob(
                  new Blob([lines.join("\n")], {
                    type: "text/csv;charset=utf-8",
                  }),
                  "rapport_classement.csv",
                );
              }}
            >
              Télécharger le rapport de classement (CSV)
            </button>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 18,
              }}
            >
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setStage("select");
                  setSortedBlob(null);
                  setUnclassifiedBlob(null);
                }}
              >
                Traiter d'autres fichiers
              </button>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={
                    busy ||
                    (!sortedBlob?.size && !unclassifiedBlob?.size) ||
                    saved
                  }
                  onClick={saveBothPdfs}
                >
                  {saved
                    ? "Enregistrés dans le dossier"
                    : "Enregistrer les deux dans le dossier"}
                </button>
                <button type="button" onClick={closeModal} disabled={busy}>
                  Terminer
                </button>
              </div>
            </div>
          </>
        )}

        {chatModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="chatgpt-modal-title"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10002,
              background: "rgba(15,23,42,.68)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              style={{
                width: "min(680px, 100%)",
                maxHeight: "88vh",
                overflowY: "auto",
                background: "#fff",
                color: "#172033",
                borderRadius: 16,
                padding: 22,
                boxShadow: "0 24px 70px rgba(0,0,0,.35)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: "#111827",
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 22,
                    fontWeight: 700,
                  }}
                >
                  ✳
                </div>
                <div style={{ flex: 1 }}>
                  <h3 id="chatgpt-modal-title" style={{ margin: 0 }}>
                    Préparer l’analyse avec ChatGPT
                  </h3>
                  <span style={{ color: "#64748b", fontSize: 12 }}>
                    Espace de préparation — UNITED MIGRANTS
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setChatModalOpen(false)}
                  aria-label="Fermer"
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    background: "#f8fafc",
                    padding: "6px 11px",
                    fontSize: 20,
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  marginTop: 18,
                  padding: 13,
                  borderRadius: 10,
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  color: "#166534",
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                <strong>API OpenAI désactivée</strong>
                <div>
                  Cette méthode n’envoie aucun document à l’API et ne consomme
                  aucun crédit API. La conversation personnelle ChatGPT
                  s’ouvrira uniquement lorsque vous cliquerez sur le bouton
                  ci-dessous.
                </div>
              </div>

              <h4 style={{ margin: "18px 0 8px" }}>
                1. Documents sélectionnés
              </h4>
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  padding: 10,
                  maxHeight: 130,
                  overflowY: "auto",
                }}
              >
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "5px 2px",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ overflowWrap: "anywhere" }}>
                      <strong>{file.name}</strong>
                    </span>
                    <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
                      {Math.max(1, Math.round(file.size / 1024))} Ko
                    </span>
                  </div>
                ))}
              </div>

              <h4 style={{ margin: "18px 0 8px" }}>
                2. Instructions d’analyse
              </h4>
              <p style={{ margin: "0 0 8px", color: "#475569", fontSize: 13 }}>
                Les instructions demandent à ChatGPT d’identifier chaque
                document, de retrouver sa date pertinente, de conserver les
                documents multipages ensemble et de produire deux PDF
                chronologiques.
              </p>
              <textarea
                readOnly
                value={chatPrompt}
                rows={9}
                onFocus={(event) => event.target.select()}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #cbd5e1",
                  borderRadius: 9,
                  padding: 11,
                  fontSize: 12,
                  lineHeight: 1.45,
                  resize: "vertical",
                }}
              />
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 9,
                  marginTop: 10,
                }}
              >
                <button type="button" onClick={copyChatGPTPrompt}>
                  Copier les instructions
                </button>
                <button
                  type="button"
                  onClick={continueToChatGPT}
                  style={{
                    background: "#166534",
                    color: "#fff",
                    border: "1px solid #166534",
                    borderRadius: 8,
                    padding: "10px 15px",
                    fontWeight: 600,
                  }}
                >
                  Continuer dans ChatGPT ↗
                </button>
                <button
                  type="button"
                  onClick={() => setChatModalOpen(false)}
                  style={{
                    marginLeft: "auto",
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    padding: "10px 15px",
                  }}
                >
                  Retour à UNITED MIGRANTS
                </button>
              </div>

              <p
                style={{
                  margin: "14px 0 0",
                  color: "#64748b",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                Important : pour des raisons de sécurité du navigateur, UNITED
                MIGRANTS ne peut pas joindre automatiquement les fichiers à
                votre compte ChatGPT. Dans ChatGPT, ajoutez les fichiers
                affichés ci-dessus, puis collez les instructions copiées.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
